// A showcase room's curves, in the shape the auralization engine hears.
//
// The listening room plays the SAME room it draws: the before/after response
// at the seat and the decay per band that the seat card plots
// (upsell/curves.generated.json, extracted from the room's solved snapshots)
// become the two impulse responses the product's own Audio preview would
// build for it (room/audio/useAuralization.ts -> @roomtreatment/auralization).
// Nothing is solved here and nothing is invented; it is a reshaping.
//
// Two conversions, both forced by what the engine reads:
//   * level: the curves are in dB relative to the room's mean, the engine
//     wants linear pressure — it re-references by its own geometric mean near
//     the crossover, so only the shape matters, not the offset;
//   * decay: one third-octave list becomes the engine's two halves — the
//     modal fine grid below its 200 Hz crossover and the band table above it.

import { MODAL_CROSSOVER_HZ } from '@roomtreatment/auralization';
import type { FreqResponseResponse, RT60Response } from '@/lib/adminTypes';
import type { ShowcaseRoom } from '@/components/upsell/rooms';
import { logSpacedHz } from '@/components/upsell/series';

export interface RoomAudioInputs {
  freq: FreqResponseResponse;
  rt60: RT60Response;
  /** The treated room's panel count: the engine's direct-to-reverberant
   *  target for "after" (irSynth.ts drrForState). */
  nProducts: number;
}

/** A room whose treated snapshot has no panels yet still has an "after"
 *  curve; the engine only builds an "after" state for a panel count above 0,
 *  so it is given the landing sample's own figure. */
const FALLBACK_PANELS = 8;

const linear = (db: number) => 10 ** (db / 20);

/** One object per room, so "is the engine already holding this room" is an
 *  identity check (useListeningAudio's `built`). */
const cache = new Map<string, RoomAudioInputs>();

export function roomAudioInputs(room: ShowcaseRoom): RoomAudioInputs {
  let inputs = cache.get(room.id);
  if (!inputs) {
    inputs = shape(room);
    cache.set(room.id, inputs);
  }
  return inputs;
}

function shape(room: ShowcaseRoom): RoomAudioInputs {
  const { lo, hi, before, after } = room.curves.freq;
  const n = before.length;
  const freqs = before.map((_, i) => logSpacedHz(lo, hi, i, n));

  const { bands, before: decayBefore, after: decayAfter } = room.curves.rt60;
  const fine = bands.flatMap((f, i) => (f <= MODAL_CROSSOVER_HZ ? [i] : []));
  const tail = bands.flatMap((f, i) => (f >= MODAL_CROSSOVER_HZ ? [i] : []));
  const table = (values: number[]) => Object.fromEntries(tail.map((i) => [String(bands[i]), values[i]]));

  return {
    freq: { freqs, mag: after.map(linear), mag_baseline: before.map(linear) },
    rt60: {
      rt60: table(decayAfter),
      rt60_baseline: table(decayBefore),
      rt60_fine: { freqs: fine.map((i) => bands[i]), rt60: fine.map((i) => decayAfter[i]) },
      rt60_fine_baseline: { freqs: fine.map((i) => bands[i]), rt60: fine.map((i) => decayBefore[i]) },
    },
    nProducts: room.doc.panels.length || FALLBACK_PANELS,
  };
}
